import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminUsers } from './components/AdminUsers';
import { Dropzone } from './components/Dropzone';
import { FileList } from './components/FileList';
import { OptionsBar } from './components/OptionsBar';
import { SignIn } from './components/SignIn';
import { SourceBar } from './components/SourceBar';
import { Stats, type BatchProgress } from './components/Stats';
import {
  convertRemote,
  deleteAllFiles,
  deleteFile,
  getConfig,
  getStats,
  listFiles,
  uploadFiles,
  zipUrl,
  type AccountStats,
  type FileDto,
  type OutputFormat,
  type ServerConfig,
} from './lib/api';
import { getSession, signOut, type AuthState } from './lib/auth';
import { pickFromDropbox } from './pickers/dropbox';
import { pickFromGoogleDrive } from './pickers/googleDrive';
import { pickFromOneDrive } from './pickers/oneDrive';
import type { PickedFile, RemoteSource } from './pickers/types';
import { isHeicName } from './pickers/types';

const DEFAULT_CONFIG: ServerConfig = {
  maxFileBytes: 50 * 1024 * 1024,
  maxFilesPerBatch: 50,
  retentionHours: 24,
  formats: ['jpeg', 'png'],
};

// The server answers a conversion request only once every file in it is done,
// so one big request means one long silence. Sending a few at a time lets the
// count, the bar and the thumbnails move while the rest is still converting.
// Five keeps the server's four decode workers busy without going quiet for long.
const UPLOAD_BATCH_SIZE = 5;

const PICKERS: Record<RemoteSource, () => Promise<PickedFile[]>> = {
  dropbox: pickFromDropbox,
  'google-drive': pickFromGoogleDrive,
  onedrive: pickFromOneDrive,
};

/** `null` from the server means this account has no cap on a selection. */
function batchSize(limit: number | null): number {
  return Math.max(1, Math.min(limit ?? UPLOAD_BATCH_SIZE, UPLOAD_BATCH_SIZE));
}

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export default function App() {
  const [auth, setAuth] = useState<AuthState | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [config, setConfig] = useState<ServerConfig>(DEFAULT_CONFIG);
  const [stats, setStats] = useState<AccountStats | null>(null);
  const [batch, setBatch] = useState<BatchProgress | null>(null);
  const [files, setFiles] = useState<FileDto[]>([]);
  const [format, setFormat] = useState<OutputFormat>(() => readStored<OutputFormat>('hct.format', 'jpeg'));
  const [quality, setQuality] = useState<number>(() => readStored<number>('hct.quality', 85));

  const [busy, setBusy] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    getSession()
      .then(setAuth)
      .catch(() => setAuth({ user: null, allowed: false, admin: false }))
      .finally(() => setAuthReady(true));
  }, []);

  const signedIn = Boolean(auth?.user && auth.allowed);

  useEffect(() => {
    if (!signedIn) return;
    getConfig().then(setConfig).catch(() => setConfig(DEFAULT_CONFIG));
    getStats().then(setStats).catch(() => undefined);
    // Files from an earlier visit are still on the server until they expire.
    // They follow the account now, so a different browser still finds them.
    listFiles()
      .then((result) => setFiles(result.files))
      .catch(() => undefined);
  }, [signedIn]);

  const handleSignOut = useCallback(async () => {
    await signOut().catch(() => undefined);
    setFiles([]);
    setStats(null);
    setBatch(null);
    setAuth({ user: null, allowed: false, admin: false });
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('hct.format', JSON.stringify(format));
      localStorage.setItem('hct.quality', JSON.stringify(quality));
    } catch {
      /* private browsing */
    }
  }, [format, quality]);

  // Keep the "deleted in ..." countdowns honest without a per-row timer.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  // Expire rows client-side at the same moment the server would drop them.
  useEffect(() => {
    setFiles((current) => {
      const live = current.filter((file) => file.expiresAt > now);
      return live.length === current.length ? current : live;
    });
  }, [now]);

  const converted = useMemo(() => files.filter((file) => file.status === 'converted'), [files]);

  const addResults = useCallback((incoming: FileDto[]) => {
    setFiles((current) => {
      const byId = new Map(current.map((file) => [file.id, file]));
      for (const file of incoming) byId.set(file.id, file);
      return [...byId.values()].sort((a, b) => a.createdAt - b.createdAt);
    });

    const failures = incoming.filter((file) => file.status === 'failed').length;
    if (failures > 0) {
      setNotice(failures + (failures === 1 ? ' file could not be converted.' : ' files could not be converted.'));
    }
  }, []);

  /* ------------------------------------------------------------- device ---- */

  const handleFiles = useCallback(
    async (selected: File[]) => {
      setError(null);
      setNotice(null);

      const accepted = selected.filter(
        (file) => isHeicName(file.name) || file.type === 'image/heic' || file.type === 'image/heif',
      );
      const skipped = selected.length - accepted.length;

      if (accepted.length === 0) {
        setError(
          skipped > 0 ? 'Those files are not HEIC or HEIF images.' : 'No files were selected.',
        );
        return;
      }
      if (skipped > 0) {
        setNotice(skipped + (skipped === 1 ? ' non-HEIC file was skipped.' : ' non-HEIC files were skipped.'));
      }

      setBusy(true);
      setProgress(0);

      const controller = new AbortController();
      abortRef.current = controller;

      const total = accepted.length;
      const size = batchSize(config.maxFilesPerBatch);
      const batches: File[][] = [];
      for (let i = 0; i < total; i += size) batches.push(accepted.slice(i, i + size));

      let done = 0;
      setBatch({ done: 0, total });
      const describe = () =>
        total === 1
          ? 'Converting 1 photo…'
          : 'Converted ' + done + ' of ' + total + ', ' + (total - done) + ' left';

      try {
        setStatusText(describe());

        for (const chunk of batches) {
          const result = await uploadFiles(chunk, {
            format,
            quality,
            signal: controller.signal,
            // Uploading fills the first half of this batch's share of the bar.
            // The rest lands when the server returns the converted files, so
            // the bar never claims work the server has not finished.
            onProgress: (fraction) => setProgress((done + fraction * chunk.length * 0.5) / total),
          });
          addResults(result.files);
          if (result.stats) setStats(result.stats);
          done += chunk.length;
          setProgress(done / total);
          setBatch({ done, total });
          setStatusText(describe());
        }
      } catch (err) {
        if (!(err instanceof DOMException && err.name === 'AbortError')) {
          setError(err instanceof Error ? err.message : 'Conversion failed');
        }
      } finally {
        abortRef.current = null;
        setBusy(false);
        setProgress(0);
        setBatch(null);
        setStatusText('');
      }
    },
    [addResults, config.maxFilesPerBatch, format, quality],
  );

  /* -------------------------------------------------------------- cloud ---- */

  const handlePick = useCallback(
    async (source: RemoteSource) => {
      setError(null);
      setNotice(null);

      try {
        const picked = await PICKERS[source]();
        if (picked.length === 0) return;

        setBusy(true);
        setProgress(0);

        const total = picked.length;
        const size = batchSize(config.maxFilesPerBatch);
        let done = 0;
        setBatch({ done: 0, total });
        setStatusText('Fetching ' + total + (total === 1 ? ' file…' : ' files…'));

        for (let i = 0; i < total; i += size) {
          const chunk = picked.slice(i, i + size);
          const result = await convertRemote(chunk, { format, quality });
          addResults(result.files);
          if (result.stats) setStats(result.stats);
          done += chunk.length;
          setProgress(done / total);
          setBatch({ done, total });
          setStatusText(
            total === 1
              ? 'Converting 1 file…'
              : 'Converted ' + done + ' of ' + total + ', ' + (total - done) + ' left',
          );
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not import from that provider');
      } finally {
        setBusy(false);
        setProgress(0);
        setBatch(null);
        setStatusText('');
      }
    },
    [addResults, config.maxFilesPerBatch, format, quality],
  );

  /* ----------------------------------------------------------- downloads ---- */

  const download = useCallback((href: string) => {
    const link = document.createElement('a');
    link.href = href;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  const handleDownload = useCallback(
    (file: FileDto) => {
      if (file.downloadUrl) download(file.downloadUrl);
    },
    [download],
  );

  const handleDownloadAll = useCallback(() => {
    download(zipUrl(converted.map((file) => file.id)));
  }, [converted, download]);

  /* ------------------------------------------------------------ deletion ---- */

  const handleDelete = useCallback(async (id: string) => {
    setFiles((current) => current.filter((file) => file.id !== id));
    await deleteFile(id).catch(() => undefined);
  }, []);

  const handleClearAll = useCallback(async () => {
    setFiles([]);
    setNotice(null);
    await deleteAllFiles().catch(() => undefined);
  }, []);

  const hasFiles = files.length > 0;

  // Nothing is rendered until the session is known, so the app never flashes
  // the converter at someone who is about to be sent to the sign-in screen.
  if (!authReady) return <div className="page" />;

  if (!signedIn) {
    return (
      <SignIn
        onSignedIn={(state) => {
          setAuth(state);
          setAuthReady(true);
        }}
      />
    );
  }

  return (
    <div className="page">
      <div className="account-bar">
        <span className="account-who">
          {auth?.user?.picture ? (
            <img className="account-avatar" src={auth.user.picture} alt="" width={24} height={24} />
          ) : null}
          {auth?.user?.email}
        </span>
        <button type="button" className="btn btn-quiet" onClick={handleSignOut}>
          Sign out
        </button>
      </div>

      <header className="masthead">
        <h1>HEIC to JPG &amp; PNG</h1>
        <p>Convert iPhone photos to a format everything can open.</p>
        <div className="retention-note">
          <span aria-hidden="true">🔒</span>
          Every file is deleted {config.retentionHours} hours after it is converted
        </div>
      </header>

      {error ? <div className="notice notice-error">{error}</div> : null}
      {notice ? <div className="notice">{notice}</div> : null}

      {/*
        Two columns: dropping files and reading results needs the width, while
        settings and totals are glanceable and sit out of the way on the right.
        The rail stacks underneath on anything narrower than a tablet.
      */}
      <div className="layout">
        <div className="column-main">
          <div className="card">
            <Dropzone
              onFiles={handleFiles}
              disabled={busy}
              maxFileBytes={config.maxFileBytes}
              maxFilesPerBatch={config.maxFilesPerBatch}
            />
            <SourceBar onPick={handlePick} busy={busy} />

            {busy ? (
              <>
                <div className="progress" role="progressbar" aria-label="Conversion progress">
                  <div className="progress-bar" style={{ width: Math.round(progress * 100) + '%' }} />
                </div>
                <div className="notice" style={{ marginTop: 12, marginBottom: 0 }}>
                  <span className="spinner" style={{ display: 'inline-block', verticalAlign: '-2px', marginRight: 8 }} />
                  {statusText || 'Working…'}
                </div>
              </>
            ) : null}
          </div>

          {hasFiles ? (
            <div className="card">
              <div className="results-head">
                <h2>
                  {converted.length} of {files.length} converted
                </h2>
                <div className="results-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={converted.length === 0}
                    onClick={handleDownloadAll}
                  >
                    Download all ({converted.length})
                  </button>
                  <button type="button" className="btn btn-quiet" onClick={handleClearAll}>
                    Clear
                  </button>
                </div>
              </div>
              <FileList files={files} now={now} onDelete={handleDelete} onDownload={handleDownload} />
            </div>
          ) : null}
        </div>

        <aside className="column-side">
          {stats && (stats.converted > 0 || batch) ? <Stats stats={stats} batch={batch} /> : null}

          <div className="card">
            <h2>Output</h2>
            <OptionsBar
              format={format}
              onFormatChange={setFormat}
              quality={quality}
              onQualityChange={setQuality}
              disabled={busy}
            />
          </div>

          {auth?.admin ? <AdminUsers /> : null}
        </aside>
      </div>

      <footer className="footer">
        Files are converted on the server and removed automatically after {config.retentionHours} hours.
        <br />
        Camera metadata, including GPS location, is stripped from every converted image.
      </footer>
    </div>
  );
}
